import { U as UniqueTags, c as TagsWithInnerContent, M as MetaTagsArrayable, H as HasElementTags, T as TagConfigKeys, D as DupeableTags, d as UsesMergeStrategy, V as ValidHeadTags } from './unhead.fg-0ge_u.mjs';
import { a as callHook } from './unhead.DvIxXxuO.mjs';

const META_NOREWRITE_RE = /^(?:viewport|description|keywords|robots)$/;
function isMetaArrayDupeKey(v) {
  return MetaTagsArrayable.has(v.split(":")[1]);
}
function dedupeKey(tag) {
  const { props, tag: t, key } = tag;
  if (UniqueTags.has(t))
    return t;
  if (t === "link" && props.rel === "canonical")
    return "canonical";
  if (t === "link" && props.rel === "alternate") {
    const altKey = props.hreflang || props.type;
    if (altKey)
      return `alternate:${altKey}`;
  }
  if (props.charset)
    return "charset";
  if (t === "meta") {
    for (const n of ["name", "property", "http-equiv"]) {
      const v = props[n];
      if (v !== void 0)
        return `meta:${v}${(typeof v !== "string" || !v.includes(":")) && !META_NOREWRITE_RE.test(v) && key ? `:key:${key}` : ""}`;
    }
  }
  if (key)
    return `${t}:key:${key}`;
  if (props.id)
    return `${t}:id:${props.id}`;
  if (t === "link" && props.rel === "alternate")
    return `alternate:${props.href || ""}`;
  return TagsWithInnerContent.has(t) && (tag.textContent || tag.innerHTML) ? `${t}:content:${tag.textContent || tag.innerHTML}` : void 0;
}
function hashTag(tag) {
  return tag._h || tag._d || tag.textContent || tag.innerHTML || `${tag.tag}:${Object.entries(tag.props).map(([k, v]) => `${k}:${String(v)}`).join()}`;
}

function walkResolver(val, resolve, key) {
  if (key === "_resolver")
    return val;
  if (typeof val === "function" && (!key || key !== "titleTemplate" && !key.startsWith("on")))
    val = val();
  const v = resolve ? resolve(key, val) : val;
  if (Array.isArray(v))
    return v.map((r) => walkResolver(r, resolve));
  if (v?.constructor === Object) {
    const next = {};
    for (const k in v) {
      if (k === "__proto__" || k === "constructor" || k === "prototype")
        continue;
      next[k] = walkResolver(v[k], resolve, k);
    }
    return next;
  }
  return v;
}

function normalizeStyleClassProps(key, value) {
  const isStyle = key === "style";
  const store = isStyle ? /* @__PURE__ */ new Map() : /* @__PURE__ */ new Set();
  const add = (v) => {
    if (!v)
      return;
    if (isStyle) {
      const i = v.indexOf(":");
      i > 0 && store.set(v.slice(0, i).trim(), v.slice(i + 1).trim());
    } else {
      v.split(" ").forEach((c) => c && store.add(c));
    }
  };
  if (typeof value === "string") {
    (isStyle ? value.split(";") : [value]).forEach(add);
  } else if (Array.isArray(value)) {
    value.forEach(add);
  } else if (value && typeof value === "object") {
    for (const k in value) {
      const v = value[k];
      v && v !== "false" && (isStyle ? store.set(k.trim(), String(v)) : add(k));
    }
  }
  return store;
}
function normalizeProps(tag, input) {
  tag.props = tag.props || {};
  if (!input)
    return tag;
  if (tag.tag === "templateParams") {
    tag.props = input;
    return tag;
  }
  const isHtmlTag = HasElementTags.has(tag.tag) || tag.tag === "htmlAttrs" || tag.tag === "bodyAttrs";
  for (const prop in input) {
    if (prop === "__proto__" || prop === "constructor" || prop === "prototype")
      continue;
    const value = input[prop];
    if (value === null) {
      tag.props[prop] = null;
    } else if (prop === "class" || prop === "style") {
      tag.props[prop] = normalizeStyleClassProps(prop, value);
    } else if (TagConfigKeys.has(prop)) {
      if ((prop === "textContent" || prop === "innerHTML") && typeof value === "object") {
        const type = input.type || "application/json";
        if (type.endsWith("json") || type === "speculationrules" || type === "importmap") {
          tag.props.type = input.type = type;
          tag[prop] = JSON.stringify(value);
        }
      } else {
        tag[prop] = value;
      }
    } else if (value !== void 0) {
      const isData = prop.startsWith("data-");
      const key = isHtmlTag && !isData ? prop.toLowerCase() : prop;
      const str = String(value);
      const isMeta = tag.tag === "meta" && key === "content";
      tag.props[key] = str === "true" || str === "" ? isData || isMeta ? str : true : !value && isData && str === "false" ? "false" : value;
    }
  }
  return tag;
}
function normalizeTag(tagName, _input) {
  const input = typeof _input === "object" && typeof _input !== "function" ? _input : { [tagName === "script" || tagName === "noscript" || tagName === "style" ? "innerHTML" : "textContent"]: _input };
  const tag = normalizeProps({ tag: tagName, props: {} }, input);
  if (tag.key && DupeableTags.has(tag.tag))
    tag.props["data-hid"] = tag._h = tag.key;
  if (tag.tag === "script" && typeof tag.innerHTML === "object") {
    tag.innerHTML = JSON.stringify(tag.innerHTML);
    tag.props.type = tag.props.type || "application/json";
  }
  return Array.isArray(tag.props.content) ? tag.props.content.map((v) => ({ ...tag, props: { ...tag.props, content: v } })) : tag;
}
function normalizeEntryToTags(input, propResolvers) {
  if (!input)
    return [];
  if (typeof input === "function")
    input = input();
  const resolvers = (key, val) => {
    for (const r of propResolvers)
      val = r(key, val);
    return val;
  };
  input = walkResolver(resolvers(void 0, input), resolvers);
  const tags = [];
  for (const key in input) {
    const value = input[key];
    if (value !== void 0) {
      for (const v of Array.isArray(value) ? value : [value]) tags.push(normalizeTag(key, v));
    }
  }
  return tags.flat();
}

const LT_RE = /</g;
const SCRIPT_END_RE = /<\/script/g;
const sortTags = (a, b) => a._w === b._w ? a._p - b._p : a._w - b._w;
function dedupeTags(ctx) {
  let hasFlatMeta = false;
  for (const next of ctx.tags.sort(sortTags)) {
    const k = next._d || hashTag(next);
    const prev = ctx.tagMap.get(k);
    if (!prev) {
      ctx.tagMap.set(k, next);
      continue;
    }
    const strategy = next.tagDuplicateStrategy || (UsesMergeStrategy.has(next.tag) ? "merge" : null) || (next.key && next.key === prev.key ? "merge" : null);
    if (strategy === "merge") {
      const props = { ...prev.props };
      for (const p in next.props) {
        props[p] = p === "style" ? new Map([...prev.props.style || /* @__PURE__ */ new Map(), ...next.props[p]]) : p === "class" ? /* @__PURE__ */ new Set([...prev.props.class || [], ...next.props[p]]) : next.props[p];
      }
      ctx.tagMap.set(k, { ...next, props });
    } else if (next._p >> 10 === prev._p >> 10 && next.tag === "meta" && isMetaArrayDupeKey(k)) {
      ctx.tagMap.set(k, Object.assign([...Array.isArray(prev) ? prev : [prev], next], next));
      hasFlatMeta = true;
    } else if (next._w === prev._w ? next._p > prev._p : next._w < prev._w) {
      ctx.tagMap.set(k, next);
    }
  }
  return hasFlatMeta;
}
function resolveTitleTemplate(ctx, head) {
  const title = ctx.tagMap.get("title");
  const tpl = ctx.tagMap.get("titleTemplate");
  head._title = title?.textContent;
  if (!tpl)
    return;
  const fn = tpl.textContent;
  head._titleTemplate = fn;
  if (!fn)
    return;
  let v = typeof fn === "function" ? fn(title?.textContent) : fn;
  if (typeof v === "string" && !head.plugins.has("template-params"))
    v = v.replace("%s", title?.textContent || "");
  if (title) {
    v === null ? ctx.tagMap.delete("title") : ctx.tagMap.set("title", { ...title, textContent: v });
  } else {
    ctx.tagMap.set("titleTemplate", { ...tpl, tag: "title", textContent: v });
  }
}
function sanitizeTags(tags) {
  return tags.filter((t) => {
    const { innerHTML, tag, props } = t;
    if (!ValidHeadTags.has(tag) || !Object.keys(props).length && !innerHTML && !t.textContent)
      return false;
    if (tag === "meta" && !props.content && !props["http-equiv"] && !props.charset)
      return false;
    if (tag === "script" && (innerHTML || t.textContent)) {
      const type = String(props.type);
      const isJsonLike = type.endsWith("json") || type === "importmap" || type === "speculationrules";
      const escape = (content) => isJsonLike ? (typeof content === "string" ? content : JSON.stringify(content)).replace(LT_RE, "\\u003C") : typeof content === "string" ? content.replace(SCRIPT_END_RE, "<\\/script") : content;
      if (innerHTML)
        t.innerHTML = escape(innerHTML);
      if (t.textContent)
        t.textContent = escape(t.textContent);
      t._d = dedupeKey(t);
    }
    return true;
  });
}
function resolveTags(head, options) {
  const weightFn = options?.tagWeight ?? head.resolvedOptions._tagWeight ?? (() => 100);
  const ctx = { tagMap: /* @__PURE__ */ new Map(), tags: [] };
  const entries = [...head.entries.values()];
  for (const e of entries) {
    if (e._pending !== void 0) {
      e.input = e._pending;
      delete e._pending;
      delete e._tags;
    }
  }
  callHook(head, "entries:resolve", { entries, ...ctx });
  for (const e of entries) {
    if (!e._tags) {
      const normalizeCtx = {
        tags: normalizeEntryToTags(e.input, head.resolvedOptions.propResolvers || []).map((t) => Object.assign(t, e.options)),
        entry: e
      };
      callHook(head, "entries:normalize", normalizeCtx);
      e._tags = normalizeCtx.tags.map((t, i) => {
        t._w = weightFn(t);
        t._p = (e._i << 10) + i;
        t._d = dedupeKey(t);
        if (!t._d)
          t._h = hashTag(t);
        return t;
      });
    }
  }
  ctx.tags = entries.flatMap((e) => (e._tags || []).map((t) => ({ ...t, props: { ...t.props } })));
  const hasFlatMeta = dedupeTags(ctx);
  resolveTitleTemplate(ctx, head);
  ctx.tags = [...ctx.tagMap.values()];
  if (hasFlatMeta)
    ctx.tags = ctx.tags.flat().sort(sortTags);
  callHook(head, "tags:beforeResolve", ctx);
  callHook(head, "tags:resolve", ctx);
  callHook(head, "tags:afterResolve", ctx);
  return sanitizeTags(ctx.tags);
}

export { dedupeTags as a, normalizeProps as b, resolveTitleTemplate as c, dedupeKey as d, hashTag as h, isMetaArrayDupeKey as i, normalizeEntryToTags as n, resolveTags as r, sanitizeTags as s, walkResolver as w };
